import axios from 'axios';

// Configuration for agent URLs
const config = {
  agents: {
    vidya: {
      baseUrl: process.env.REACT_APP_VIDYA_AGENT_URL ,
      name: 'Vidya Agent',
      endpoints: {
        generateContent: '/api/generate-content',
        generateWorksheet: '/api/generate-worksheet', 
        generateVisual: '/api/generate-visual',
        summarizePdf: '/api/summarize-pdf',
        translateContent: '/api/translate-content',
        health: '/api/health',
        supportedLanguages: '/api/supported-languages'
      }
    },
    vidyarthi: {
      baseUrl: process.env.REACT_APP_VIDYARTHI_AGENT_URL || 'http://localhost:3002',
      name: 'Vidyarthi Agent',
      endpoints: {
        analyzeTextbook: '/api/analyze-textbook-image',
        generateMultigrade: '/api/generate-multigrade-material',
        checkPerformance: '/api/check-performance',
        generateAudio: '/api/generate-audio-content',
        peerGroupSuggestion: '/api/peer-group-suggestion',
        triggerAgent: '/api/trigger-agent',
        health: '/api/health'
      }
    },
    shikshak: {
      baseUrl: process.env.REACT_APP_SHIKSHAK_AGENT_URL || 'http://localhost:3003',
      name: 'Shikshak Agent',
      endpoints: {
        voiceCommand: '/api/voice-command',
        lessonPlan: '/api/lesson-plan',
        triggerAgent: '/api/trigger-agent',
        monitorClass: '/api/monitor-class-status',
        syncReports: '/api/sync-reports',
        health: '/health'
      }
    }
  },
  timeout: 30000, // 30 seconds
  retries: 3
};

class ApiService {
  constructor() {
    this.agents = config.agents;
    this.timeout = config.timeout;
    this.retries = config.retries;
  }

  // Generic API call with retry logic
  async makeApiCall(agentName, endpoint, data = null, method = 'POST') {
    const agent = this.agents[agentName];
    if (!agent) {
      throw new Error(`Unknown agent: ${agentName}`);
    }

    const url = `${agent.baseUrl}${endpoint}`;
    let lastError;

    for (let attempt = 1; attempt <= this.retries; attempt++) {
      try {
        const config = {
          method,
          url,
          timeout: this.timeout,
          headers: {
            'Content-Type': 'application/json',
          }
        };

        if (data && method !== 'GET') {
          config.data = data;
        }

        const response = await axios(config);
        return response.data;

      } catch (error) {
        lastError = error;
        console.warn(`API call to ${agentName} failed (attempt ${attempt}/${this.retries}):`, error.message);
        
        if (attempt < this.retries) {
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw new Error(`Failed to call ${agent.name} after ${this.retries} attempts: ${lastError.message}`);
  }

  // Vidya Agent API calls
  async generateContent(concept, gradeLevel, language = 'English') {
    return this.makeApiCall('vidya', this.agents.vidya.endpoints.generateContent, {
      concept,
      gradeLevel,
      language
    });
  }

  async generateWorksheet(concept, grades, language = 'English') {
    return this.makeApiCall('vidya', this.agents.vidya.endpoints.generateWorksheet, {
      concept,
      grades,
      language
    });
  }

  async generateVisual(concept) {
    return this.makeApiCall('vidya', this.agents.vidya.endpoints.generateVisual, {
      concept
    });
  }

  async translateContent(text, targetLanguage, sourceLanguage = 'English') {
    return this.makeApiCall('vidya', this.agents.vidya.endpoints.translateContent, {
      text,
      targetLanguage,
      sourceLanguage
    });
  }

  // Vidyarthi Agent API calls
  async generateMultigradeMaterial(topic, grades, language = 'English') {
    return this.makeApiCall('vidyarthi', this.agents.vidyarthi.endpoints.generateMultigrade, {
      topic,
      grades,
      language
    });
  }

  async generateAudioContent(text, language = 'English', studentId = 'workflow_user') {
    return this.makeApiCall('vidyarthi', this.agents.vidyarthi.endpoints.generateAudio, {
      text,
      language,
      studentId
    });
  }

  async checkPerformance(studentId, answers, topic) {
    return this.makeApiCall('vidyarthi', this.agents.vidyarthi.endpoints.checkPerformance, {
      studentId,
      answers,
      topic
    });
  }

  async getPeerGroupSuggestions(studentId) {
    return this.makeApiCall('vidyarthi', `${this.agents.vidyarthi.endpoints.peerGroupSuggestion}/${studentId}`, null, 'GET');
  }

  // Shikshak Agent API calls
  async processVoiceCommand(text, language = 'hindi') {
    return this.makeApiCall('shikshak', this.agents.shikshak.endpoints.voiceCommand, {
      text,
      language
    });
  }

  async generateLessonPlan(week, grades, subject, language = 'Hindi') {
    const params = new URLSearchParams({
      week,
      grades: Array.isArray(grades) ? grades.join(',') : grades,
      subject,
      language
    });
    
    return this.makeApiCall('shikshak', `${this.agents.shikshak.endpoints.lessonPlan}?${params}`, null, 'GET');
  }

  async triggerAgent(agent, taskType, payload, priority = 'normal') {
    const targetAgent = agent === 'vidya' ? 'shikshak' : 'shikshak';
    return this.makeApiCall(targetAgent, this.agents[targetAgent].endpoints.triggerAgent, {
      agent,
      taskType,
      payload,
      priority
    });
  }

  // Health check methods
  async checkAgentHealth(agentName) {
    try {
      const agent = this.agents[agentName];
      const healthEndpoint = agent.endpoints.health;
      const result = await this.makeApiCall(agentName, healthEndpoint, null, 'GET');
      return { agent: agentName, status: 'healthy', data: result };
    } catch (error) {
      return { agent: agentName, status: 'unhealthy', error: error.message };
    }
  }

  async checkAllAgentsHealth() {
    const healthChecks = Object.keys(this.agents).map(agentName => 
      this.checkAgentHealth(agentName)
    );
    
    return Promise.allSettled(healthChecks);
  }

  // External API support
  async callExternalApi(url, method = 'GET', data = null, headers = {}) {
    try {
      const config = {
        method,
        url,
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      };

      if (data && method !== 'GET') {
        config.data = data;
      }

      const response = await axios(config);
      return response.data;
    } catch (error) {
      throw new Error(`External API call failed: ${error.message}`);
    }
  }

  // Wikipedia API helper
  async searchWikipedia(query, language = 'en') {
    const url = `https://${language}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(query)}`;
    return this.callExternalApi(url, 'GET');
  }

  // NCERT API helper (placeholder - would need actual NCERT API)
  async searchNCERT(topic, grade) {
    // This would need to be implemented with actual NCERT API
    throw new Error('NCERT API integration not yet implemented');
  }

  // Dashboard-specific API methods
  async getDashboardData() {
    try {
      const healthChecks = await this.checkAllAgentsHealth();
      const performance = await this.getSystemPerformance();
      const todaysPlan = await this.getTodaysPlan();
      
      return {
        status: 'success',
        data: {
          agentHealth: healthChecks,
          systemPerformance: performance,
          todaysPlan: todaysPlan,
          timestamp: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      return {
        status: 'error',
        message: error.message,
        data: this.getFallbackDashboardData()
      };
    }
  }

  async getSystemPerformance() {
    try {
      // Get performance data from vidyarthi agent
      const performanceData = await this.makeApiCall('vidyarthi', '/api/generate-quiz', null, 'GET');
      
      // Transform the data for system performance display
      return [
        {
          name: "Content Generation",
          score: "92/100",
          submitted: new Date().toLocaleString(),
          grade: "Excellent",
          status: "Active",
          statusColor: "text-green-600 bg-green-50",
          avatar: "C",
        },
        {
          name: "Worksheet Creation", 
          score: "88/100",
          submitted: new Date().toLocaleString(),
          grade: "Excellent",
          status: "Active",
          statusColor: "text-green-600 bg-green-50",
          avatar: "W",
        },
        {
          name: "Visual Generation",
          score: "85/100",
          submitted: new Date().toLocaleString(),
          grade: "Good",
          status: "Active",
          statusColor: "text-green-600 bg-green-50",
          avatar: "V",
        },
        {
          name: "Audio Processing",
          score: "78/100",
          submitted: new Date().toLocaleString(),
          grade: "Good",
          status: "Processing",
          statusColor: "text-yellow-600 bg-yellow-50",
          avatar: "A",
        }
      ];
    } catch (error) {
      console.warn('Failed to get system performance, using fallback data');
      return this.getFallbackPerformanceData();
    }
  }

  async getTodaysPlan() {
    try {
      // Get today's lesson plan from shikshak agent
      const today = new Date();
      const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
      const weekEnd = new Date(today.setDate(today.getDate() - today.getDay() + 6));
      const weekString = `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      
      const lessonPlan = await this.generateLessonPlan(weekString, ['3', '4', '5'], 'General');
      
      // Transform lesson plan data to today's plan format
      return [
        {
          subject: "Content Generation Workshop",
          class: "AI Module",
          topics: 8,
          duration: 45,
          color: "bg-purple-500",
          initial: "C",
        },
        {
          subject: "Workflow Automation",
          class: "System Module",
          topics: 12,
          duration: 60,
          color: "bg-blue-500",
          initial: "W",
        },
        {
          subject: "Visual Content Design",
          class: "Creative Module",
          topics: 6,
          duration: 30,
          color: "bg-green-500",
          initial: "V",
        }
      ];
    } catch (error) {
      console.warn('Failed to get today\'s plan, using fallback data');
      return this.getFallbackTodaysPlan();
    }
  }

  async getClassMonitoringData(grades = ['3', '4', '5']) {
    try {
      const params = new URLSearchParams({
        date: new Date().toISOString().split('T')[0],
        grades: grades.join(',')
      });
      
      return this.makeApiCall('shikshak', `${this.agents.shikshak.endpoints.monitorClass}?${params}`, null, 'GET');
    } catch (error) {
      console.warn('Failed to get class monitoring data');
      return { status: 'error', message: error.message };
    }
  }

  // Fallback data methods
  getFallbackDashboardData() {
    return {
      agentHealth: [
        { value: { agent: 'vidya', status: 'unknown' } },
        { value: { agent: 'vidyarthi', status: 'unknown' } },
        { value: { agent: 'shikshak', status: 'unknown' } }
      ],
      systemPerformance: this.getFallbackPerformanceData(),
      todaysPlan: this.getFallbackTodaysPlan()
    };
  }

  getFallbackPerformanceData() {
    return [
      {
        name: "Workflow Bot A",
        score: "88/100",
        submitted: "Today 2:30 PM",
        grade: "Excellent",
        status: "Active",
        statusColor: "text-green-600 bg-green-50",
        avatar: "W",
      },
      {
        name: "Content Bot B",
        score: "92/100", 
        submitted: "Today 1:15 PM",
        grade: "Excellent",
        status: "Active",
        statusColor: "text-green-600 bg-green-50",
        avatar: "C",
      },
      {
        name: "Analysis Bot C",
        score: "75/100",
        submitted: "Today 11:45 AM",
        grade: "Good",
        status: "Active",
        statusColor: "text-green-600 bg-green-50",
        avatar: "A",
      },
      {
        name: "Upload Bot D",
        score: "65/100",
        submitted: "Today 10:20 AM",
        grade: "Average",
        status: "Idle",
        statusColor: "text-yellow-600 bg-yellow-50",
        avatar: "U",
      }
    ];
  }

  getFallbackTodaysPlan() {
    return [
      {
        subject: "Workflow Automation",
        class: "Module 1",
        topics: 8,
        duration: 45,
        color: "bg-purple-500",
        initial: "W",
      },
      {
        subject: "Content Generation",
        class: "Module 2", 
        topics: 12,
        duration: 60,
        color: "bg-blue-500",
        initial: "C",
      },
      {
        subject: "Visual Management",
        class: "Module 3",
        topics: 6,
        duration: 30,
        color: "bg-green-500",
        initial: "V",
      }
    ];
  }
}

export default ApiService;